// @ts-strict-ignore
/**
 * @module CallLog
 */

import { Subject, Subscription, filter } from "rxjs";
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';

import { ErrorHelperService } from "../utilities/errorHelper.service";

import { CallLog, CallLogRB, CallLogType } from 'rainbow-web-sdk/models/callLog.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { CallDirection } from "rainbow-web-sdk/models/call.model";
import { User } from "rainbow-web-sdk/models/user.model";

import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';

import { WebrtcGatewayService, WEBRTC_GATEWAY_SVC } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';

import { TelephonyService, TELEPHONY_SVC } from 'rainbow-web-sdk/services/telephony/telephony.service';

import { PersonalDirectoryEvent, PersonalDirectoryService, PERSONAL_DIRECTORY_SVC } from 'rainbow-web-sdk/services/directory/personal-directory.service';
import { OFFICE365_AD_SVC, Office365AdService } from 'rainbow-web-sdk/services/directory/office365ad.service';
import { COMPANY_DIRECTORY_SVC, CompanyDirectoryService } from 'rainbow-web-sdk/services/company/company-directory.service';
import anonymizer from "../../utils/anonymizer";
import { $iq, $msg } from "strophe.js";

/** @internal */
export const CALL_LOG_SVC = 'CallLogService';

/**
 * @eventProperty
 * CallLog service events send to other services
 * Can listen to it via `CallLogService.subscribe()` API
 */
export enum CallLogServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when the call log history has been updated in one of the following ways:
     * - Reception of a new call log from the server (in the form of a notification, and not in response to an explicit fetch request).
     * - User request to delete call logs.\
     *   This covers cases of deletion of a specific call log, all call logs associated with a user, or the user's entire call log history.\
     * This event contains no data, the current call logs history have to be retrieved by using the getCallLogs() API.
     */
    ON_CALLLOG_UPDATED = "ON_CALLLOG_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the total number of call logs for the connected user has been updated.
     *
     * **Event Data**
     * - **counter**: The total number of call logs
     */
    ON_CALLLOG_TOTAL_NUMBER_UPDATED = "ON_CALLLOG_TOTAL_NUMBER_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the counter of missed calls has been updated.
     *
     * **Event Data**
     * - **counter**: The number of missed calls
     */
    ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED = "ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED",
}

/**
 * @ignore
 * @eventProperty
 * CallLog service internal events send to other sevices via sendInnerEvent()
 * Should not be used by SDK user
 */
export enum CallLogServiceInnerEvents {

    /**
     * @eventProperty
     * This RB event is sent when at least one call log present in the internal cache has been updated after a change
     * in the Personal Directory of the user.
     * By change, we mean the creation, modification or removal of a personal contact leading to the update of the display information
     * associated to a call log item such as the display name or the associated contact details.
     * This event does not cover any update in the call logs list.
     */
    RAINBOW_ON_CALLLOG_INFOS_UPDATED = "RAINBOW_ON_CALLLOG_INFOS_UPDATED",
}

/**
 * Defines the options to manage the XMPP request to fetch Call Logs from server
 */
interface IFetchOptions {
    numberOfElementsToGet?: number;
    useBefore?: string;
    useAfter?: string;
}

/**
 * Defines the contacts that be associated to 1 call log item associated to an log not related to a user RB but to
 * a directory contact, an external number on the public network ...
 * 'contact' specified the contact who will be associated first with the call log, one of the following:
 * - A directory contact found with the name resolution information (directory name + id) done on server side and provided by the server.
 * - A new created temporary contact (call log for external call only) if no directory contact has been found,
 * 'backupContact' specified the contact who will be used as backup if at some point the directory specified in 'contact'  no longer exists.
 * It refers to a new created temporary contact (call log for external call only).
 */
interface IExternalLogManagedContacts {
    contact: Contact;
    backupContact: Contact;
}

export interface CallLogService {

    /**
     * Subscribe to the CallLog updates, all events are of RBEvent<CallLogServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<CallLogServiceEvents>) => any, eventNames?: CallLogServiceEvents | CallLogServiceEvents[]): Subscription;

    /**
     * Indicates whether the CallLog Service has been initialized or not.
     * If this is not the case, no call log has yet been retrieved.
     * @returns True if the CallLog Service has been intitialized; False otherwise.
     */
    isInitialized(): boolean;

    /**
     * Initializes the CallLog service for the user by returning the most recent call logs.
     * The number of call logs to be returned can optionnaly be specified in the request
     * Additional call logs have to be retrieved furthermore via the API 'getNextCallLogs'.
     * @param number - (optional) Number of call logs to retrieve at initialization step.
     * This parameter is optional and set by default to 100.
     * The allowed range of values is [1..250].
     * If the value provided is greater, the maximum value will be used.
     * If the value is 0 or less, the request will be rejected and an error thrown.
     * @returns Returns a Promise with an array of the first retrieved call logs
     * @throws Error - In the event of the specified parameter is invalid.
     */
    initCallLogs(number: number): Promise<CallLog[]>;

    /**
     * Get the current content of the call logs history.
     * The request can be made at any time to retrieve the call logs already known by the Call Log service.
     * It is also advisable to make this request when an 'ON_CALLLOG_UPDATED' event has been received notifying you
     * that the call logs history has been updated following an operation such as the reception of a brand new call log,
     * the modification (e.g., marking as read) or the deletion of existing call log(s).
     * @returns Returns a Promise with an array of the call logs already known by the Call Log service
     */
    getCallLogs(): Promise<CallLog[]>;

    /**
     * Get additional call logs.
     * The number of additional call logs to be returned can be specified in the request
     * @param number - (optional) Number of additional call logs to get.
     * This parameter is optional and set by default to 100.
     * The allowed range of values is [1..250].
     * If the value provided is greater, the maximum value will be used.
     * If the value is 0 or less, the request will be rejected and an error thrown.
     * @returns Returns a Promise with an array of the call logs already known by the Call Log service and the newly retrieved.
     * @throws Error - In the event of the specified parameter is invalid.
     */
    getNextCallLogs(number: number): Promise<CallLog[]>;

    /**
     * Return the number total of call logs available for the connected user
     */
    getCallLogsCounter(): number;

    /**
     * Returns the number total of missed calls.
     * Missed call is a call with the following properties: state is 'missed', direction is 'incoming' and read flag is 'false'.
     * This API can be called before the first call logs are retrieved, i.e. before the initialization step.
     */
    getMissedCallsCounter(): number;

    /**
     * Mark a call log item specified by its object instance as read
     * @param callLog - The call log instance to mark as read
     */
    markCallLogAsRead(callLog: CallLog): void;

    /**
     * Mark all call logs already known by the Call Log service as read
     * This concerns the call logs fetched from server during the initialization step and through additional fetch requests.
     */
    markAllCallsLogsAsRead(): void;

    /**
     * Delete all call logs associated with a user
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     */
    deleteCallLogsForUser(user: User): Promise<any>;

    /**
     * Delete the call log specified by its object instance
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     * @param callLog - The call log instance to delete
     */
    deleteCallLog(callLog: CallLog): Promise<any>;

    /**
     * Delete all the call logs history of the connected user
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     */
    deleteAllCallLogs(): Promise<any>;
}

/**
 * @ignore
 */
export class CallLogServiceRB extends Service implements CallLogService {
    private static CALL_LOG_NAMESPACE: string = 'jabber:iq:telephony:call_log';
    private static CALL_LOG_ACK_NAMESPACE: string = 'urn:xmpp:telephony:call_log:receipts';
    private static CALL_LOG_NOTIF_NAMESPACE: string = 'jabber:iq:notification:telephony:call_log';
    private static NB_DEF_UCAAS_CALL_LOG: number = 75;
    private static NB_MAX_NEXT_CALL_LOGS: number = 250;
    private static NB_DEF_CALL_LOGS: number = 100;

    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private mainService: MainService = MainService.getInstance();

    // Injectable services, not always available
    private telephonyService?: TelephonyService = undefined;
    private webrtcGatewayService?: WebrtcGatewayService = undefined;
    private personalDirectoryService?: PersonalDirectoryService = undefined;
    private office365AdService?: Office365AdService = undefined;
    private companyDirectoryService: CompanyDirectoryService = undefined;

    // Injectable services
    private centralizedService: any;

    private initialized: boolean = false;
    private fetchServerXMPPError: boolean = false;

    // Observables for events sent by CallLogService
    private rxSubject: Subject<any> = new Subject<any>();
    private innerRxSubject: Subject<RBEvent> = new Subject<RBEvent>();

    private xmppConnectionSubscription: Subscription = null;
    private personalDirSubscriptions: Subscription[] = [];

    private callLogHandlerRef: any = null;
    private callLogMessageAckRef: any = null;
    private callLogNotificationRef: any = null;
    private usePhonebookDirectory: boolean = false;
    private callLogPromises: Promise<any>[] = [];
    private numberMissedCallLogs: number = 0;
    private numberTotalCallLogs: number = 0;
    // Counter of duplicated call log (same "call_id" stored as call_log.id)
    private numberDuplicatedCallLogs: number = 0;
    private callLogs: CallLogRB[] = [];
    private aggregatedCallLogs: CallLogRB[] = [];
    private firstTimeStamp: string;
    private lastTimestamp: string;
    private startPromise: Promise<any>;
    private startPromiseResolve: any;
    private ackAllCallLogsInDBDone: boolean = false;

    // Flag to avoid to launch several fetch next Call logs requests
    private fetchingCallLogs: boolean = false;

    // Flag to remember that the fetch request comes from the user (initCallLogs, getNextCallLogs)
    // and then to know if in addition to the call logs data, an event should be sent or not as final result.
    private userFetchRequest: boolean = false;

    /**
     * Returns the Call Log singleton
     */
    public static getInstance(): CallLogServiceRB {
        return Service.create(CALL_LOG_SVC, CallLogServiceRB);
    }

    public static build(): CallLogServiceRB {
        return new CallLogServiceRB();
    }

    private constructor() {
        super();
    }

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------

    /**
     * This async method has to be call to start the CallLogs service
     */
    public async start(): Promise<void> {
        try {
            this.logger.info(``);
            this.logger.info(`[CallLogService] === STARTING ===`);

            // Dynamically inject services (should not be here)
            this.centralizedService = this.mainService.inject('CentralizedService');
            this.telephonyService = this.mainService.inject<TelephonyService>(TELEPHONY_SVC);
            this.webrtcGatewayService = this.mainService.inject<WebrtcGatewayService>(WEBRTC_GATEWAY_SVC);
            this.personalDirectoryService = this.mainService.inject<PersonalDirectoryService>(PERSONAL_DIRECTORY_SVC);
            this.office365AdService = this.mainService.inject<Office365AdService>(OFFICE365_AD_SVC);
            this.companyDirectoryService = this.mainService.inject<CompanyDirectoryService>(COMPANY_DIRECTORY_SVC);

            this.usePhonebookDirectory = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK) || config.permitSearchFromPhoneBook;

            this.attachHandlers();

            this.logger.info(`[CallLogService] === STARTED ===`);
        }
        catch (error) {
            this.logger.error(`[CallLogService] === STARTING FAILURE === ${error.message}`);
        }
    }

    /**
     * This async method has to be call to stop the CallLogs service
     */
    public async stop(): Promise<void> {
        this.logger.info('[CallLogService] === STOPPING ===');

        this.resetCallLogsData();
        this.initialized = false;
        this.ackAllCallLogsInDBDone = false;

        this.removeXMPPListeners();

        if (this.personalDirSubscriptions) {
            this.personalDirSubscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        }

        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); }

        this.logger.info('[CallLogService] === STOPPED ===');
    }

    private attachHandlers(): void {
        this.logger.info(`[CallLogService] attachHandlers`);

        // Attach or re-attach XMPP listeners when xmppService is connected
        if (this.xmppService.connected) {
            this.attachXMPPListeners();
            this.getMissedCallsCounterFromServer();
        }

        this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents(async (event: RBEvent) => {
            if (event.name === 'ON_XMPP_CONNECTED') {
                this.attachXMPPListeners();
                this.getMissedCallsCounterFromServer();
                if (event.data.reason === 'RECONNECTION')
                    if (this.initialized) {
                        this.logger.info(`[CallLogService] ON_XMPP_CONNECTED -- service already initialized - Get History Page`);
                        this.getCallLogHistoryPage(this.lastTimestamp);
                    } else {
                        this.logger.info(`[CallLogService] ON_XMPP_CONNECTED -- service already not initialized`);
                        if (this.fetchServerXMPPError) {
                            // There was an xmpp disconnection when initializing the service and retrieving the first N call logs.
                            this.logger.warn(`[CallLogService] ON_XMPP_CONNECTED -- service initialization was in progress, restart it!`);
                            this.fetchServerXMPPError = false;
                            await this.initialize();
                        }
                    }
            }

            if (event.name === 'ON_XMPP_DISCONNECTED') {
                this.logger.warn(`[CallLogService] ON_XMPP_DISCONNECTED -- Reason = ${event.data.reason}`);
                if (event.data.reason === 'LOGOUT') {
                    // Reset the call logs data
                    this.resetCallLogsData();
                    this.initialized = false;
                    this.ackAllCallLogsInDBDone = false;
                }
                else if (event.data.reason === 'NO_NETWORK') {
                    // Reset the initial request to fetch the first call logs and so, considered the service as no more initialized
                    if (this.startPromise) {
                        this.initialized = false;
                        this.startPromiseResolve();
                        this.startPromiseResolve = null;
                        this.startPromise = undefined;
                        this.fetchServerXMPPError = true;
                    }
                }
            }
        });

        if (this.personalDirectoryService) {
            this.personalDirSubscriptions.push(this.personalDirectoryService.subscribe((event: RBEvent) => { this.onPersonalContactEvent(event); }));
        }
    }

    private removeXMPPListeners() {
        if (this.callLogHandlerRef) {
            this.xmppService.connection.deleteHandler(this.callLogHandlerRef);
            this.callLogHandlerRef = null;
        }

        if (this.callLogNotificationRef) {
            this.xmppService.connection.deleteHandler(this.callLogNotificationRef);
            this.callLogNotificationRef = null;
        }

        if (this.callLogMessageAckRef) {
            this.xmppService.connection.deleteHandler(this.callLogMessageAckRef);
            this.callLogMessageAckRef = null;
        }
    }

    private attachXMPPListeners() {
        this.logger.info(`[CallLogService] attachXMPPListeners`);

        this.removeXMPPListeners();

        this.callLogHandlerRef = this.xmppService.connection.addHandler((stanza: any) => { return this.onHistoryCallLogMessageReceived(stanza); }, CallLogServiceRB.CALL_LOG_NAMESPACE, null, null);
        this.callLogNotificationRef = this.xmppService.connection.addHandler((stanza: any) => { return this.onCallLogNotificationReceived(stanza); }, CallLogServiceRB.CALL_LOG_NOTIF_NAMESPACE, null, null);
        this.callLogMessageAckRef = this.xmppService.connection.addHandler((stanza: any) => { return this.onCallLogAckReceived(stanza); }, CallLogServiceRB.CALL_LOG_ACK_NAMESPACE, null, null);
    }

    private resetCallLogsData(missedIncluded: boolean = true) {
        this.logger.info(`[CallLogService] resetCallLogsData -- missedCallLogs data included = ${missedIncluded}`);

        if (missedIncluded) { this.numberMissedCallLogs = 0; }
        this.numberTotalCallLogs = 0;
        this.numberDuplicatedCallLogs = 0;
        this.callLogs = [];
        this.aggregatedCallLogs = [];
    }

    /**
     * Subscribe to the CallLog updates, all events are of RBEvent<CallLogServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<CallLogServiceEvents>) => any, eventNames?: CallLogServiceEvents | CallLogServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<CallLogServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<CallLogServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[FavoritesService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * Call log service events send to other services via sendEvent()
     * Can listen to it via CallLogServiceRB.subscribe() API
     * @param name - The name of the event
     * @param data - The data to be send (an object)
     */
    private sendEvent(name: CallLogServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /**
     * Subscribe to updates from the service (all events are of RBEvent type {name: string, data:any});
     * Here's a list of events received :
     *   - RAINBOW_ON_CALLLOG_INFOS_UPDATED
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     */
    public subscribeToInnerEvents(handler: any): Subscription {
        return this.innerRxSubject.subscribe(handler);
    }

    /**
     * Call log service events send to other services via sendEvent()
     * Can listen to it via CallLogServiceRB.subscribe() API
     * @param name - The name of the event
     * @param data - The data to be send (an object)
     */
    private sendInnerEvent(name: string, data: any = null) {
        this.innerRxSubject.next(RBEvent.create(name, data));
    }

    /**
     * Initialize the Call Logs service on the 1st request to fetch call logs from the server
     * Only the following methods are allowed to initialize the call logs, i.e. to retrieve them for the first time from the server
     * - getCalllogs
     * - getAggregatedCalllogs
     * The consistency  of the number of missed calls logs is also check
     * @param initFetchOptions - [optional] Options to fect the initial call logs from server
     */
    private async initialize(initFetchOptions: IFetchOptions = {}): Promise<any> {
        // Return a resolved promise if the call logs have already been initialized
        if (this.initialized)
            return Promise.resolve();

        this.logger.info(`[CallLogService] initialize -- Not yet initialized, fetch first call logs from server`);

        // In case there are more than one client that tries to initialize the service at the same time
        // We return a Promise (not an error) that the Call Logs will well be initialized.
        // Moreover, no need to check 'fetchingCallLogs' flag as the multiple access is checked thanks to 'startPromise'
        // and once initialized, the call logs are retrieved directly from cache, not from server.
        if (this.startPromise)
            return this.startPromise;

        this.startPromise = new Promise((resolve) => {
            this.startPromiseResolve = resolve;

            // For security and to get started properly, reset Call logs data (Internal buffer & counters)
            // EXCEPT the missed calls counter retrived at startup of the service.
            // The call logs already present in buffer (for example those associated to calls received before going in 'Calls' menu)
            // will be retrieved when fetching logs from server for the first time.
            this.resetCallLogsData(false);

            this.logger.info(`[CallLogService] initialize -- initFetchOptions: Number=${initFetchOptions.numberOfElementsToGet} - before=${initFetchOptions.useBefore} - useAfter=${initFetchOptions.useAfter}`);
            this.fetchCallLogsFromServer(initFetchOptions);
            this.logger.info(`[CallLogService] initialize -- numberMissedCallLogs = ${this.numberMissedCallLogs}`);
            this.logger.info(`[CallLogService] initialize -- ackAllCallLogsInDBDone = ${this.ackAllCallLogsInDBDone}`);

            if (this.numberMissedCallLogs > 14 && !this.ackAllCallLogsInDBDone) {
                this.ackAllCallLogsInDB();
            }
        });

        return this.startPromise;
    }

    // ------------------------------------------------------------
    //                  MAM REQUESTS
    // ------------------------------------------------------------

    /**
     * Get the number of missed calls logs (state === "missed" && direction === CallDirection.INCOMING) from the server
     * This method can be used before retrieving all the callLogs from the server (by using fetch)
     * An event 'ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED' is sent with the number of missed calls logs.
     */
    private async getMissedCallsCounterFromServer(): Promise<number> {
        this.logger.debug(`[CallLogService] getMissedCallsCounterFromServer`);

        const message = $iq({ from: this.xmppService.jid, type: 'set' });

        message.c('query', { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE });
        message.c("set", { xmlns: "http://jabber.org/protocol/rsm" });
        message.c("max").t('0').up().up();
        message.c("x", { xmlns: "jabber:x:data", type: "submit" });
        message.c("field", { var: "read" }).c("value").t("false").up().up();

        // Send the Request and wait for the answer
        const stanza = await this.xmppService.sendIQ(message);
        const stanzaElem: XMLElement = xmlParser(stanza);
        this.numberMissedCallLogs = parseInt(stanzaElem.find("count").text(), 10);

        this.logger.info(`[CallLogService] getMissedCallsCounterFromServer -- numberMissedCallLogs = ${this.numberMissedCallLogs}`);

        // Notify about the change of the call log counters
        this.sendEvent(CallLogServiceEvents.ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED, this.numberMissedCallLogs);

        return this.numberMissedCallLogs;
    }

    /**
     * Retrieve from server N call logs (specified by NB_DEF_UCAAS_CALL_LOG) time-stamped after the specified date
     * This method is called at reconnection after a XMPP disconnection.
     * @param useAfter - [optional] A date after which the call logs must be retrieved
     */
    private async getCallLogHistoryPage(useAfter: any = null): Promise<any> {
        this.logger.debug(`[CallLogService] getCallLogHistoryPage`);

        const fetchOptions: IFetchOptions = {};

        fetchOptions.numberOfElementsToGet = CallLogServiceRB.NB_DEF_UCAAS_CALL_LOG;
        if (useAfter) { fetchOptions.useAfter = useAfter; }

        await this.fetchCallLogsFromServer(fetchOptions);
    }

    /**
     * Retrieve (via a XMPP request to server) N call log from the server
     * after a specified timestamp (priority date criteria) or before a specified timestamp
     * @param options - fetch options
     */
    private async fetchCallLogsFromServer(options: IFetchOptions): Promise<any> {
        this.logger.debug(`[CallLogService] fetchCallLogsFromServer -- FetchOptions: NbElem=${options.numberOfElementsToGet} - before=${options.useBefore} - useAfter=${options.useAfter}`);

        const message = $iq({ from: this.xmppService.jid, type: 'set' });
        message.c('query', { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE });
        message.c("set", { xmlns: "http://jabber.org/protocol/rsm" });

        if (options.numberOfElementsToGet) {
            // For security, avoid to fetch too much call logs from server (even if it limits the number of responses)
            if (options.numberOfElementsToGet > CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS) {
                options.numberOfElementsToGet = CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS;
            }
            message.c("max").t(options.numberOfElementsToGet.toString()).up();
        } else {
            message.c("max").t(CallLogServiceRB.NB_DEF_UCAAS_CALL_LOG.toString()).up();
        }

        if (options.useAfter) {
            message.c("after").t(options.useAfter).up();
        }
        else {
            if (options.useBefore) {
                if (this.firstTimeStamp) {
                    message.c("before").t(this.firstTimeStamp).up();
                } else {
                    message.c("before").t("").up();
                }
            }
            else {
                message.c("before").t("").up();
            }
        }

        try {
            await this.xmppService.sendIQ(message);
        } catch (error) {
            this.logger.error(`[CallLogService] fetchCallLogsFromServer sendIQ error -- ${error}`);
            // If the request was the initial request to fetch the first call logs, we reset all associated data
            // to consider again the service as no more initialized
            if (this.startPromise) {
                this.logger.error(`[CallLogService] fetchCallLogsFromServer sendIQ error during call logs initialization (1st fetch request)`);
                this.initialized = false;
                this.startPromiseResolve();
                this.startPromiseResolve = null;
                this.startPromise = undefined;
                this.fetchServerXMPPError = true;
                throw error;
            }
        }
    }

    // ------------------------------------------------------------
    //              EVENT LISTENERS ( Handling of events)
    // ------------------------------------------------------------

    /**
     * Management of XMPP Message regarding namespace 'jabber:iq:telephony:call_log' such as
     * - Number of call logs for the current user
     * - Number of unread call logs
     * - Reception of a call log item following a fetch request
     * @param stanza - The received stanza.
     */
    private async onHistoryCallLogMessageReceived(stanza: any): Promise<any> {
        const stanzaElem: XMLElement = xmlParser(stanza);

        if (stanzaElem.find("call_log").length > 0) {
            //newCallLog++ Could have several call_log elts but only process call_log with good name space
            // disable camel_case rule for this line as it's Strophe related

            // stanzaElem.find("call_log").each((__index, call_log: any) => { //parse every call_log elts
            stanzaElem.find("call_log").forEach((callLogElem: XMLElement) => { //parse every call_log elts
                const goodNameSpace = (callLogElem.attr("xmlns") === CallLogServiceRB.CALL_LOG_NOTIF_NAMESPACE);
                const nbChildElements = callLogElem.children().length;

                if (goodNameSpace && nbChildElements > 0) {
                    this.callLogPromises.push(this.createCallLogFromStanza(stanzaElem, callLogElem));
                }
            });//newCallLog--
            //this.callLogPromises.push(this.createCallLogFromStanza(stanzaElem));//newCallLog
        }
        else if (stanzaElem.find("count").length > 0 && stanzaElem.find("last").length === 0 && stanzaElem.find("first").length === 0) {
            // SAME server response (Shame! Shame!) for IQ about
            // - The number of missed calls
            // - The total number of call logs is equal to 0
            // Ex: <iq ... <query xmlns='jabber:iq:telephony:call_log'><set xmlns='http://jabber.org/protocol/rsm'><count>3</count></set></query></iq>
            // We should do nothing here !
            this.logger.info(`[CallLogService] onHistoryCallLogMessageReceived -- IQ count received`);

            // Case of start-up for a user with no call-log (ex: new user), we also come to this case here, so resolve the start promise if any
            if (this.startPromise) {
                this.initialized = true;
                this.startPromiseResolve();
                this.startPromiseResolve = null;
                this.startPromise = null;
            }
        }
        else if (stanzaElem.find("count").length > 0 && stanzaElem.find("query").length > 0 && (stanzaElem.find("last").length > 0 || stanzaElem.find("count").text() === "0" || stanzaElem.find("first").length > 0)) {
            if (stanzaElem.find("last").length) {
                this.lastTimestamp = stanzaElem.find("last").text();
            }

            if (stanzaElem.find("first").length) {
                this.firstTimeStamp = stanzaElem.find("first").text();
            }
            this.logger.info(`[CallLogService] onHistoryCallLogMessageReceived -- all calllogs received`);

            await Promise.all(this.callLogPromises).catch(() => { });
            this.callLogPromises = [];
            this.orderCallLogs();
            if (this.startPromise) {
                this.initialized = true;
                this.startPromiseResolve();
                this.startPromiseResolve = null;
                this.startPromise = null;
            }

            // Update the counter of number total of call logs available on server thanks to the last received xmpp message
            // Ex: <set xmlns='http://jabber.org/protocol/rsm'><count>324</count><first>1648814869983196</first><last>1654695242548637</last></set>
            // Done only once the call logs have been stored in the cache to avoid double counting
            if (stanzaElem.find("count").length > 0) {
                this.numberTotalCallLogs = parseInt(stanzaElem.find("count").text(), 10);
                // Adjusts the counter to take account of duplicated call logs that have been ignored
                if (this.numberDuplicatedCallLogs > 0) {
                    this.logger.info(`[CallLogService] onHistoryCallLogMessageReceived --- Number of duplicated/ignored call logs = ${this.numberDuplicatedCallLogs} `);
                    this.numberTotalCallLogs -= this.numberDuplicatedCallLogs;
                    // For security (To avoid management of negative counter)
                    if (this.numberTotalCallLogs < 0) {
                        this.numberTotalCallLogs = 0;
                    }
                }

                this.sendEvent(CallLogServiceEvents.ON_CALLLOG_TOTAL_NUMBER_UPDATED, this.numberTotalCallLogs);
                this.logger.debug(`[CallLogService] onHistoryCallLogMessageReceived -- Send event ON_CALLLOG_TOTAL_NUMBER_UPDATED: ${this.numberTotalCallLogs}`);
            }

            setTimeout(async () => {
                //check if we still have unread messages (case of blocked messages in the DB)
                const unreadMessages = await this.getMissedCallsCounterFromServer();
                if (unreadMessages) {
                    this.logger.info(`[CallLogService] onHistoryCallLogMessageReceived -- all calllogs received - still unread = ${unreadMessages}`);
                    this.ackAllCallLogsInDB();
                }
            }, 5000);
        }
        else if (stanzaElem.find("deleted").length > 0) {
            // Management of IQ <deleted count='X' xmlns='jabber:iq:telephony:call_log'/>
            // X = 1 if deletion of a single call log (extended mode)
            // X >= 1 if deletion of all call logs associated to a contact (condensed mode)
            // Deleted call logs may be call logs already retrieved OR NOT (i.e. not yet retrived though 'View More') on client side
            // So we have to update 'numberTotalCallLogs' counter (Number of elt remove from this.callLogs may be different)
            const nbDeleted: string = stanzaElem.find("deleted").attr("count");
            if (nbDeleted) {
                const nbCallLogDeleted: number = parseInt(nbDeleted, 10);
                if (nbCallLogDeleted > 0) {
                    this.numberTotalCallLogs -= nbCallLogDeleted;

                    // For security (Can happen if the server deletes more call logs than it has stored in the cache ?)
                    if (this.numberTotalCallLogs < 0) {
                        this.logger.info(`[CallLogService] onHistoryCallLogMessageReceived -- Incorrect value for numberTotalCallLogs: ${this.numberTotalCallLogs} - Reset it to 0 !`); // TEMP
                        this.numberTotalCallLogs = 0;
                    }

                    // Event sent so that the management of 'View More' button is correct
                    this.sendEvent(CallLogServiceEvents.ON_CALLLOG_TOTAL_NUMBER_UPDATED, this.numberTotalCallLogs);
                    this.logger.debug(`[CallLogService] onHistoryCallLogMessageReceived -- Send event ON_CALLLOG_TOTAL_NUMBER_UPDATED`); // TEMP
                }
            }
        }
        return true;
    }

    /**
     * Management of XMPP Message regarding namespace 'urn:xmpp:telephony:call_log:receipts'
     * Message received as an acknowledge following a call log operation 'mark-as-read' previously sent to the server
     * In the internal call logs table, the concerned call logs (identified by received "call_id" attribute(s)) are marked as as read.
     * This operation is done once a XMPP message in the format
     *      <read xmlns='urn:xmpp:telephony:call_log:receipts' call_id='web_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'/>
     * is received from the server.
     * Then the number of missed calls logs is checked again on internal cache and associated counter may be updated
     * If the case, an event 'ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED' is sent with the new value of the missed calls logs.
     * @param stanza - The received stanza.
     */
    private onCallLogAckReceived(stanza: any): boolean {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);

            if (stanzaElem.find("read").length > 0) {
                const msgId = stanzaElem.find("read").attr("call_id");
                this.callLogs.forEach((callLog: CallLogRB) => {
                    if (callLog.id === msgId) {
                        callLog.read = true;
                        callLog.isMissed = false;
                        return;
                    }
                });

                const missedCallLogsInCache = this.getMissedCallLogsInCacheCounter();
                if (missedCallLogsInCache !== this.numberMissedCallLogs) {
                    this.logger.info(`[CallLogService] onCallLogAckReceived -- Missed Call Logs Total=${this.numberMissedCallLogs} -- In Cache=${missedCallLogsInCache}`);
                    this.numberMissedCallLogs = missedCallLogsInCache;
                    this.sendEvent(CallLogServiceEvents.ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED, this.numberMissedCallLogs);
                }
            }
        }
        catch (error) {
            this.logger.error(`[CallLogService] onCallLogAckReceived -- failure with error ${error}`);
            return true;
        }
        return true;
    }

    /**
     * Management of XMPP Message regarding namespace 'jabber:iq:notification:telephony:call_log' such as
     * - Reception of a new call log item from server (as notification, not on request)
     * - Deletion of one or more call logs (single call log, call log, call logs associated to a contact)
     * This operation is done once a XMPP message with a "deleted_call_log" elem or a "updated_call_log" elem is received from the server.
     * @param stanza - The received stanza.
     */
    private async onCallLogNotificationReceived(stanza: any): Promise<boolean> {
        const stanzaElem: XMLElement = xmlParser(stanza);

        try {
            // Example:
            // <message xmlns='jabber:client'
            //     to='fb062501edb04fb899fb790fdcd449ef@openrainbow.net'
            //     from='openrainbow.net'
            //     type='chat'
            //     id='11874986261899137651'>
            //     <deleted_call_log call_id='web_87a3eaf9-699f-42aa-ae79-4aa107c1b885'
            //                 xmlns='jabber:iq:notification:telephony:call_log'/>
            // </message>
            if (stanzaElem.find("deleted_call_log").length > 0) {
                const peer = stanzaElem.find("deleted_call_log").attr("peer");
                const callId = stanzaElem.find("deleted_call_log").attr("call_id");
                if (peer) {
                    this.removeCallLogsForUser(peer);
                    this.orderCallLogs();
                } else if (callId) {
                    this.removeOneCallLog(callId);
                    this.orderCallLogs();
                } else { // Previous request to delete all the call logs thanks to 'Trash' button
                    // Here, only callLogs cache is cleared, aggregateCalllogs cache will be rebuilt afterwards
                    // The counters must not be updated here: Missed call counter remains unchanged
                    // The total nb of call logs  will be updated at reception of XMPP notif with "deleted" stanza elem
                    // So no call to resetCallLogsData() must be done here !
                    this.callLogs = [];
                    // Must be performed in order to send event ON_CALLLOG_UPDATED
                    this.orderCallLogs();
                    await this.getCallLogHistoryPage();
                }
            }
            else if (stanzaElem.find("updated_call_log").length > 0) {
                const callLogAdded: boolean = await this.createCallLogFromStanza(stanzaElem, null).catch(() => { });

                // Order the call logs cache and send event only if a new call log has really been created
                if (callLogAdded) {
                    this.orderCallLogs();
                    this.sendEvent(CallLogServiceEvents.ON_CALLLOG_TOTAL_NUMBER_UPDATED, this.numberTotalCallLogs);
                } else {
                    // Adapts the counter if this call log was seen as a duplicate and therefore ignored
                    this.numberTotalCallLogs -= this.numberDuplicatedCallLogs;
                    // For security (To avoid management of negative counter)
                    if (this.numberTotalCallLogs < 0) {
                        this.numberTotalCallLogs = 0;
                    }
                }
            }
        }
        catch (error) {
            this.logger.error(`[CallLogService] onCallLogNotificationReceived -- failure with error ${error}`);
            return true;
        }
        return true;
    }

    /**
     * Management of personal directory service events concerning the addition, modification and deletion (single or complete) of personal contacts
     * The call logs will be scanned to see if a name resolution can be performed for entries that only have a displayed phone number.
     * A name resolution in the personal directory can also be updated (in the case of a change in names, for example)
     * or even deleted if the associated number is no longer linked to a personal contact.
     * The "association" of a call log item with a personal contact is done via the data 'directoryContactId'
     * @param event - The received stanza.
     */
    private onPersonalContactEvent(event: RBEvent): boolean {
        try {
            let callLogsUpdated: boolean = false;

            switch (event.name) {
                case PersonalDirectoryEvent.PERSONAL_DIRECTORY_ADD:
                    if (event.data) {
                        const addedContact: Contact = event.data;
                        this.logger.debug(`[CallLogService] onPersonalContactEvent PERSONAL_DIRECTORY_ADD: Id = ${addedContact.id} -- Display name = ${addedContact.displayName}`);
                        this.callLogs.forEach((callLog: CallLogRB) => {
                            // Check if a call log can be associated with this new personal contact
                            // Only for call log not already linked to a personal directory contact
                            // and having a temporary contact associated
                            // and having not a display name corresponding to the called/calling number (external call without name or name resolution)
                            // if (!callLog.directoryContactId && callLog.user.temp && (callLog.user.name.value === callLog.user.phonePro)) {
                            if (!callLog.directoryContactId && callLog.user.temp) {
                                const jid = callLog.user.id;
                                const persoContact = this.personalDirectoryService.getPersonalDirectoryContactFromNumber(jid);
                                if (persoContact) {
                                    this.logger.debug(`[CallLogService] onPersonalContactEvent -- Update display of Call log number '${callLog.user.nameForLogs}' to name '${persoContact.displayName}'`);
                                    callLog.user.updateName(persoContact.firstname, persoContact.lastname);
                                    callLog.directoryContactId = persoContact.id;
                                    callLogsUpdated = true;
                                }
                            }
                        });
                        // Necessary to refresh the component and especially the user infos on right panel
                        if (callLogsUpdated)
                            this.sendInnerEvent(CallLogServiceInnerEvents.RAINBOW_ON_CALLLOG_INFOS_UPDATED);
                    }
                    break;

                case PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE:
                    if (event.data) {
                        const updatedContact: Contact = event.data;
                        this.logger.debug(`[CallLogService] onPersonalContactEvent PERSONAL_DIRECTORY_UPDATE: Id = ${updatedContact.id} -- Display name = ${updatedContact.displayName}`);
                        this.callLogs.forEach((callLog: CallLogRB) => {
                            // If the name of the contact has changed, we update simply the displayed name.
                            // But if one of the contact numbers has changed, we must check if it is still relevant to associate
                            // the call log with this personal contact
                            // If not, We go back to a situation of a number without an associated name
                            // 'phoneProCan' used by default as displayName (done in callLogCell component) when no name available
                            // As we have no indicatin about what has changed, we do a complete check again
                            if (callLog.directoryContactId === updatedContact.id) {
                                const jid = callLog.user.id;
                                const persoContact = this.personalDirectoryService.getPersonalDirectoryContactFromNumber(jid);
                                if (persoContact) {
                                    // The updated personal contact can still be used for the call log
                                    // Update of the displayed name
                                    this.logger.debug(`[CallLogService] onPersonalContactEvent -- Update display of Call log name '${callLog.user.nameForLogs}' to '${persoContact.displayName}'`);
                                    callLog.user.updateName(persoContact.firstname, persoContact.lastname);
                                    callLog.directoryContactId = persoContact.id;
                                    callLogsUpdated = true;
                                }
                                else if (callLog.directoryContactId) {
                                    // We go back to a situation of a number without an associated name
                                    // 'phoneProCan' used by default as displayName (done in callLogCell component) when no name available
                                    this.logger.debug(`[CallLogService] onPersonalContactEvent -- Reinit display of Call log name '${callLog.user.nameForLogs}' to number '${callLog.user.phoneProCan}'`);
                                    callLog.user.displayName = callLog.user.phoneProCan;
                                    callLog.user.avatar = { src: "/assets/skins/rainbow/images/conversations/unknownUser.jpeg" };
                                    callLog.directoryContactId = null;
                                    callLogsUpdated = true;
                                }
                            }
                        });
                        // Necessary to refresh the component and especially the user infos on right panel
                        if (callLogsUpdated)
                            this.sendInnerEvent(CallLogServiceInnerEvents.RAINBOW_ON_CALLLOG_INFOS_UPDATED);
                    }
                    break;

                case PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE:
                    if (event.data) {
                        const deletedContactId: string = event.data;
                        this.logger.debug(`[CallLogService] onPersonalContactEvent PERSONAL_DIRECTORY_DELETE: Id =  ${deletedContactId}`);
                        this.callLogs.forEach((callLog: CallLogRB) => {
                            // We only look for call log entries that have used this personal directory and process cleaning for this one
                            if (callLog.directoryContactId === deletedContactId) {
                                // We go back to a situation of a number without an associated name
                                // 'phoneProCan' used by default as displayName (done in callLogCell component) when no name available
                                this.logger.debug(`[CallLogService] onPersonalContactEvent -- Reinit display of Call log name '${callLog.user.nameForLogs}' to number '${callLog.user.phoneProCan}'`);
                                callLog.user.displayName = callLog.user.phoneProCan;
                                callLog.user.avatar = { src: "/assets/skins/rainbow/images/conversations/unknownUser.jpeg" };
                                callLog.directoryContactId = null;
                                callLogsUpdated = true;
                            } else if (callLog.identityContactId === deletedContactId) {
                                this.logger.error(`[CallLogService] onPersonalContactEvent - Used callback contact for Call log name: ${callLog.user.nameForLogs}, number:${callLog.user.phoneProCan} `);
                                callLog.user = callLog.userOriginBackup;
                                callLogsUpdated = true;
                            }
                        });
                        // Necessary to refresh the component and especially the user infos on right panel
                        if (callLogsUpdated)
                            this.sendInnerEvent(CallLogServiceInnerEvents.RAINBOW_ON_CALLLOG_INFOS_UPDATED);
                    }
                    break;

                case PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE_ALL:
                    this.logger.debug(`[CallLogService] onPersonalContactEvent PERSONAL_DIRECTORY_DELETE_ALL`);
                    this.callLogs.forEach((callLog: CallLogRB) => {
                        // Process all call logs that have been associated with a personal contact
                        if (callLog.directoryContactId) {
                            // We go back to a situation of a number without an associated name
                            // 'phoneProCan' used by default as displayName (done in callLogsCell.html) when no name available
                            this.logger.debug(`[CallLogService] onPersonalContactEvent -- Reinit display of Call log name '${callLog.user.displayName}' to number '${callLog.user.phoneProCan}'`);
                            callLog.user.displayName = callLog.user.phoneProCan;
                            callLog.user.avatar = { src: "/assets/skins/rainbow/images/conversations/unknownUser.jpeg" };
                            callLog.directoryContactId = null;
                            callLogsUpdated = true;
                        } else if (callLog.identityContactId) {
                            this.logger.error(`[CallLogService] onPersonalContactEvent - Used callback contact for Call log name: ${callLog.user.nameForLogs}, number:${callLog.user.phoneProCan} `);
                            callLog.user = callLog.userOriginBackup;
                            callLogsUpdated = true;
                        }
                    });
                    // Necessary to refresh the component and especially the user infos on right panel
                    if (callLogsUpdated)
                        this.sendInnerEvent(CallLogServiceInnerEvents.RAINBOW_ON_CALLLOG_INFOS_UPDATED);
                    break;

                default:
                    break;
            }
        }
        catch (error) {
            this.logger.error(`[CallLogService] onPersonalContactEvent -- failure with error ${error}`);
            return true;
        }
        return true;
    }

    // ------------------------------------------------------------
    //                  PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    // ---------------------------
    // GLOBAL
    // ---------------------------

    /**
     * Indicates whether the CallLog Service has been initialized or not.
     * If this is not the case, no call log has yet been retrieved.
     * @returns True if the CallLog Service has been initialized; False otherwise.
     */
    public isInitialized(): boolean {
        return this.initialized;
    }

    // ---------------------------
    // METHODS TO FETCH CALL LOGS
    // ---------------------------

    /**
     * Return an array of available call logs, each item representing one call log.
     * By available, we means first the call logs already recovered after the first initialization following a request to the server
     * to get the N most recent one. And then the call logs that have been added following new notification(s) from server.
     * Also some call logs may have been deleted following a user action.
     * Note that if it has not yet been done, the initialization phase will be automatically performed by retrieving
     * a certain number of logs from server (Fetch options defined by default by CallLogService are used)
     */
    public async getCalllogs(): Promise<CallLog[]> {
        this.logger.info(`[CallLogService] getCalllogs -- init status = ${this.initialized}`);
        await this.initialize();
        return this.callLogs;
    }

    /**
     * Return an array of aggregated call logs, each item representing one more call logs grouped by contact.
     * The aggregated call logs are deduced from the available call logs (obtained with the 'getCalllogs' method) but the items
     * are grouped by contact according to different information such as status ("missed" or not), direction ("incoming", "outgoing")
     * Note that if it has not yet been done, the initialization phase will be automatically performed by retrieving
     * a certain number of logs from server (Fetch options defined by default by CallLogService are used)
     */
    public async getAggregatedCalllogs(): Promise<CallLogRB[]> {
        this.logger.info(`[CallLogService] getAggregatedCalllogs -- init status = ${this.initialized}`);
        await this.initialize();
        return this.aggregatedCallLogs;
    }

    /**
     * Request to retrieve new call logs from the server.
     * The number of new call logs to retrieve can be specified.
     * This API returns the whole call logs, those already retrieved by previous requests and the new one.
     * Each call log will contain some selected and simplified information .
     * Note that the initialization phase must have been done previously. If this is not the case, an error is returned
     * @param numberOfElts - Number of new call logs to fetch
     */
    public async fetchNextPage(numberOfElts: number = CallLogServiceRB.NB_DEF_UCAAS_CALL_LOG): Promise<CallLog[]> {
        this.logger.info(`[CallLogService] fetchNextPage -- init status = ${this.initialized}, number requested = ${numberOfElts}`);

        const fetchOptions: IFetchOptions = {};

        // A request for the following call logs is now denied if the service has not been initialized through one of the following APIs:
        //  - getCalllogs
        //  - getAggregatedCalllogs
        if (!this.initialized) {
            const error = new Error("[CallLogService] Fetch next page error - The service is not initialized through one of the APIs authorized to do so");
            this.logger.error(error.message);
            return Promise.reject(error);
        }

        // Check is user has already requested next call logs from server. If the case, the new request is ignored
        if (this.fetchingCallLogs) {
            // Another fetch of the Call logs is in progress ... the current request will be rejected
            // Is the prmoise rejection  the good reaction ?
            const error = new Error("[CallLogService] Fetch next page error - Request ignored. Call log retrieval is currently in progress.");
            this.logger.error(error.message);
            return Promise.reject(error);
        }

        this.fetchingCallLogs = true;

        // Security Check (Should not happen !)
        // The number of call logs to retrieve from the server is limited to NB_MAX_NEXT_CALL_LOGS (250)
        if (numberOfElts > CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS) {
            numberOfElts = CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS;
        }
        fetchOptions.numberOfElementsToGet = numberOfElts;

        // To get the newest/latest call logs first, we need to use 'before'.
        // First request should contain empty <before> tag to get the latest messages
        // All the following requests should include <first> data in <before> tag.
        // firstTimeStamp is filled by onHistoryCallLogMessageReceived()
        if (this.firstTimeStamp) { fetchOptions.useBefore = this.firstTimeStamp; }

        await this.fetchCallLogsFromServer(fetchOptions);
        this.fetchingCallLogs = false;
        return await this.orderCallLogsHistory();
    }

    /**
     * Return an customized array of call logs with selected and simplified item information.
     * The customized call logs are deduced from the available call logs
     */
    private async orderCallLogsHistory(): Promise<CallLogRB[]> {
        this.logger.debug(`[CallLogService] orderCallLogsHistory`);

        await Promise.all(this.callLogPromises).catch(() => { });
        this.callLogPromises = [];

        // make sure that call logs are sorted by date. getTime is used to avoid type errors (TS)
        this.callLogs = this.callLogs.sort((a, b) => b.date.getTime() - a.date.getTime());

        return this.callLogs;
    }

    // ---------------------------------
    // METHODS ABOUT CALL LOGS COUNTERS
    // ---------------------------------

    /**
     * Returns the number total of missed calls.
     * Missed call is a call with the following properties: state is 'missed', direction is 'incoming' and read flag is 'false'.
     * This API can be called before the first call logs are retrieved, i.e. before the initialization step.
     */
    public getMissedCallsCounter(): number {
        this.logger.debug(`[CallLogService] getMissedCallsCounter -- Number returned = ${this.numberMissedCallLogs}`);
        return this.numberMissedCallLogs;
    }

    /**
     * Return the number total of call logs available for the connected user
     */
    public getCallLogsCounter(): number {
        this.logger.debug(`[CallLogService] getCallLogsCounter -- Number returned = ${this.numberTotalCallLogs}`);
        return this.numberTotalCallLogs;
    }

    /**
     * Get the number of missed calls (state === "missed" && direction === CallDirection.INCOMING) for call logs
     * present in the internal cache ONLY.
     * This counter may be different to the counter known by the server which covers all call logs available on its side
     */
    private getMissedCallLogsInCacheCounter(): number {
        let num = 0;
        this.callLogs.forEach((callLog) => {
            if (!callLog.read && callLog.state === "missed" && callLog.direction === CallDirection.INCOMING) {
                num++;
            }
        });

        this.logger.debug(`[CallLogService] getMissedCallLogsInCacheCounter -- Number returned = ${num}`);

        return num;
    }

    // ----------------------------------
    // METHODS TO MARK CALL LOGS AS READ
    // ----------------------------------

    // HANDLE User action and manage XMPP Request to server
    // ----------------------------------------------------

    /**
     * Mark a call log item specified by its object instance as read
     * @param calllog - The call log instance to mark as read
     */
    public markCallLogAsRead(callLog: CallLog): void {
        try {
            if (!callLog?.id) {
                throw Error("Unable to mark call log as read -- Missing call log id value");
            }
            this.logger.info(`[CallLogService] markCallLogAsRead -- id = ${callLog.id}`);

            const message = $msg({ from: this.xmppService.jid, to: this.xmppService.jid, id: "web_" + crypto.randomUUID() });
            // disable camel_case rule for this line as it's Strophe related (for 'call_id')

            message.c("read", { xmlns: CallLogServiceRB.CALL_LOG_ACK_NAMESPACE, call_id: callLog.id });
            this.xmppService.send(message);
        } catch (error) {
            this.logger.error(`[CallLogService] markCallLogAsRead -- failure: ${error.message}`);
            throw error;
        }
    }

    /**
     * Mark all call logs already known by the Call Log service as read
     * This concerns the call logs fetched from server during the initialization step and through additional fetch requests.
     */
    public markAllCallsLogsAsRead(): void {
        this.logger.info(`[CallLogService] markAllCallsLogsAsRead`);

        this.callLogs.forEach((callLog: CallLog) => {
            if (!callLog.read) {
                const message = $msg({ from: this.xmppService.jid, to: this.xmppService.jid, id: "web_" + crypto.randomUUID() });
                // disable camel_case rule for this line as it's Strophe related (for 'call_id')

                message.c("read", { xmlns: CallLogServiceRB.CALL_LOG_ACK_NAMESPACE, call_id: callLog.id });
                this.xmppService.send(message);
            }
        });
    }

    // Internal management of Unread message
    // ----------------------------------------------------

    /**
     * Ack all call logs on server side.
     */
    private async ackAllCallLogsInDB(): Promise<any> {
        try {
            if (this.ackAllCallLogsInDBDone) {
                this.logger.info(`ackAllCallLogsInDB --- aready done -- ignore`);
                return;
            }

            this.ackAllCallLogsInDBDone = true;
            this.logger.warn(`ackAllCallLogsInDB --- used for ${this.numberMissedCallLogs} call-logs`);
            const message = $iq({ from: this.xmppService.jid, type: 'set' });
            message.c('markallread', { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE });
            const stanza = await this.xmppService.sendIQ(message);
            const stanzaElem: XMLElement = xmlParser(stanza);

            if (stanzaElem.find("error").length > 0) {
                this.logger.warn(`ackAllCallLogsInDB --- error ...`);
                return;
            }
            await this.getMissedCallsCounterFromServer();
        }
        catch {
            this.logger.warn(`ackAllCallLogsInDB --- error ...`);
            return;
        }
    }

    // ----------------------------
    // METHODS TO DELETE CALL LOGS
    // ----------------------------

    // HANDLE User action and manage XMPP Request to server
    // ----------------------------------------------------

    /**
     * Process the deletion of all call logs associated with a directory contact.
     * The contactId (i.E. the userId) is not managed by the xmpp server in "delete" IQ.
     * It will be necessary to carry out a deletion via the phone numbers used by the directory contact.
     * @param contact - The directory contact instance to delete
     */
    private deleteCallLogsForDirectoryContact(contact: Contact): void {
        if (!contact) {
            return;
        }

        this.logger.debug(`[CallLogService] deleteCallLogsForDirectoryContact -- directory contact id = ${contact.id}`);

        // New array containing only the entries linked to the directory contact for which it has asked to delete the call logs.
        const directoryContactCallLogs: CallLog[] = [...this.callLogs.filter(callLog => callLog.identityContactId === contact.id)];

        // Set containing telephone numbers defined in the directory contact and for which a call has been recorded.
        // A request will be made to the server to delete the call logs associated with these telephone numbers.
        const directoryContactPhoneNumbers = new Set<string>();

        directoryContactCallLogs.forEach((callLog: CallLogRB) => {
            this.logger.debug(`[CallLogService] deleteCallLogsForDirectoryContact -- call log tmp id = ${callLog.userOriginBackup.id}`);
            // Add in the set the phone number originally used to reach the contact.
            // Information provided by the server (xmpp message CALL_LOG_NOTIF_NAMESPACE) and stored in the 'backup' temp contact
            // to keep the main original information.
            directoryContactPhoneNumbers.add(callLog.userOriginBackup.id);
        });

        // The phone numbers found will be used as a key to delete the call logs
        directoryContactPhoneNumbers.forEach(async (phoneNumber) => {
            const message = $iq({ from: this.xmppService.jid, to: this.xmppService.jid, type: "set" });
            message.c("delete", { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE, peer: phoneNumber });
            await this.xmppService.sendIQ(message);
        });
    }

    /**
     * Delete all call logs associated to a user
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     */
    public async deleteCallLogsForUser(user: User): Promise<any> {
        const userId = user.jid ? user.jid : user.id;

        this.logger.info(`[CallLogService] deleteCallLogsForUser -- user id used = ${userId}`);

        // Check if the request concerns the deletion of all call logs associated with a directory contact.
        // If this is the case, the deletion process required a special process.
        if (this.isDirectoryContact(user as Contact)) {
            this.deleteCallLogsForDirectoryContact(user as Contact);
        } else {
            const message = $iq({ from: this.xmppService.jid, to: this.xmppService.jid, type: "set" });
            message.c("delete", { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE, peer: userId });
            this.xmppService.sendIQ(message);
        }
    }

    /**
     * Delete the call log specified by its object instance
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     * @param callLog - The call log instance to delete
     */
    public async deleteCallLog(callLog: CallLog): Promise<any> {
        this.logger.info(`[CallLogService] deleteCallLog -- call log id = ${callLog.id}`);

        const message = $iq({ from: this.xmppService.jid, to: this.xmppService.jid, type: "set" });
        // disable camel_case rule for this line as it's Strophe related (for 'call_id')

        const msg = message.c("delete", { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE, call_id: callLog.id });
        return this.xmppService.sendIQ(msg);
    }

    /**
     * Delete the call log specified by its id
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     * @param id - The call log identifier
     */
    public async deleteCallLogById(id: string): Promise<any> {
        this.logger.info(`[CallLogService] deleteCallLogById -- call log id = ${id}`);

        const message = $iq({ from: this.xmppService.jid, to: this.xmppService.jid, type: "set" });
        // disable camel_case rule for this line as it's Strophe related (for 'call_id')

        const msg = message.c("delete", { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE, call_id: id });
        return this.xmppService.sendIQ(msg);
    }

    /**
     * Delete all the call logs history of the connected user
     * A ON_CALLLOG_UPDATED event is sent afterwards at the completion of the operation to notify about the call logs history update.
     */
    public async deleteAllCallLogs(): Promise<any> {
        this.logger.info(`[CallLogService] deleteAllCallLogs`);

        const message = $iq({ from: this.xmppService.jid, to: this.xmppService.jid, type: "set" });
        message.c("delete", { xmlns: CallLogServiceRB.CALL_LOG_NAMESPACE });
        return this.xmppService.sendIQ(message);
    }

    // HANDLE XMPP Response from server
    // --------------------------------

    /**
     * Remove in the internal call logs table one call log specified by its id
     * This operation is done once a XMPP message with a "peer" elem and "call_id" attribute is received from the server.
     * @param jid - The contact's jid
     */
    private removeCallLogsForUser(jid: string): void {
        this.logger.debug(`[CallLogService] removeCallLogsForUser -- user jid = ${jid}`);

        if (jid.endsWith("@_")) { jid = jid.substring(0, jid.length - 2); }

        const newLogs = [];
        for (let i = 0; i < this.callLogs.length; i++) {
            let addCallLog: boolean = false;

            if (!this.callLogs[i].user || ((this.callLogs[i].user.jid !== jid) && (this.callLogs[i].user.id !== jid))) {
                // By default, retains the current call log in cache if it is different from the peer received from server and
                // associated to deleted call log
                addCallLog = true;

                // Additional processing: The current call log entry concerns a call log where name resolution has been done by server
                // from a directory (personal, business...).
                // In this case, the deletion was made by specifying a phone number and not a user id/jid.
                // As this number is indicated by 'jid' parameter, we have to parse userOriginBackup contact to find such number.
                // If found, the current call log entry will not be retained.
                if ((this.callLogs[i].identityContactId)
                    && (this.callLogs[i]?.userOriginBackup?.id === jid)) {
                    // this.logger.debug(`[CallLogService] removeCallLogsForUser -- Call log entry ${i} associated to directory removed `);
                    addCallLog = false;
                }

                if (addCallLog) {
                    newLogs.push(this.callLogs[i]);
                }
            }

        }
        this.callLogs = newLogs;
    }

    /**
     * Remove in the internal call logs table the call log item specified by its callId
     * This operation is done once a XMPP message with a "deleted_call_log" elem and "call_id" attribute is received from the server.
     * @param callId - Call log id
     */
    private removeOneCallLog(callId: string) {
        this.logger.debug(`[CallLogService] removeOneCallLog --  Call log id = ${callId}`);

        this.callLogs = this.callLogs.filter(el => el.id !== callId);
    }

    // ------------------------------------------------------------
    // METHODS TO CREATE & MANAGE CALL LOGS cache inside service
    // ------------------------------------------------------------

    /**
     * If the remote user is not an RB user, we will analyse whether we have any identifying information (names), as follows:
     *  - Checks whether a name resolution has been performed on the server side
     *    This requires attributes "origin" and "id" to be provided in the element "identity"
     *  - If such name resolution done and a valid directory contact has been retrieved, this contact is also returned as result
     *  - A temporary contact associated with the current call log is created  in all cases: No name resolution done on server side,
     *    provided information did not allow a directory contact to be retrieved (Invalid Id) or directory contact found
     *  - Check whether name information can be retrieved in the following way:
     *      - Presence of stanza attributes linked to names: "firstName"/"lastName"/"displayName"
     *      - Search in Outlook if allowed
     * @param callLogElem - The received call log element
     * @param otherPartyNumber - The phone number of the remote party
     * Either the external number called by the Rainbow user OR the phone number that called the user
     * @returns Promise that resolves with contact(s) that will be linked to the call log entry
     */
    private async getOrCreateContactWithNameResolution(callLogElem: XMLElement, otherPartyNumber: string): Promise<IExternalLogManagedContacts> {
        let contact: Contact;
        let directoryContact: Contact;
        let identityFoundAndValid: boolean = false;

        const identity = callLogElem.find("identity");

        // Use of Directories (PBX Phonebook, Personal Dir, Business Dir, O365) allowed and identity (xNames) information available in call log
        if (this.usePhonebookDirectory && identity?.length) {
            const origin = callLogElem.find("identity").attr("origin");
            const contactId = callLogElem.find("identity").attr("userId");

            // If a name resolution has been done on server side, we will use the information provided in stanza 'userId' and 'origin'
            // indicating in which directory the contact was found and its identifier.
            if (contactId && origin && origin !== "RAINBOW") { // Avoid processing if name resolution concerns a RB user
                this.logger.info(`[CallLogService] getContactWithNameResolution - Server name resolution on directory: ${origin}, contactId: ${contactId}`);
                try {

                    switch (origin) {
                        // Name resolution done in Microsoft O365 Azure directory
                        case "O365":
                            directoryContact = await this.office365AdService.getO365ContactFromServer(contactId);
                            if (!directoryContact) {
                                this.logger.info(`[CallLogService] getContactWithNameResolution - O365 contact ${contactId} no more valid`);
                            }
                            break;

                        // Name resolution done in Personal directory
                        case "PERSONAL_DIRECTORY":
                            directoryContact = await this.companyDirectoryService.getDirectoryContactById(contactId);
                            if (!directoryContact) {
                                this.logger.info(`[CallLogService] getContactWithNameResolution - Personal contact ${contactId} no more valid`);
                            }
                            break;

                        // Name resolution done in Business directory
                        case "BUSINESS_DIRECTORY":
                            directoryContact = await this.companyDirectoryService.getDirectoryContactById(contactId);
                            if (!directoryContact) {
                                this.logger.info(`[CallLogService] getContactWithNameResolution - Business contact ${contactId} no more valid`);
                            }
                            break;

                        // Name resolution done in PBX phonebook
                        case "PBX":
                            // Case managed to avoid error case in default
                            break;

                        case "OUTLOOK":
                            directoryContact = await this.office365AdService.getOutlookContactById(contactId);
                            if (!directoryContact) {
                                this.logger.info(`[CallLogService] getContactWithNameResolution - Outlook contact ${contactId} no more valid`);
                            }
                            break;

                        // Unknown directory received, not possible to manage it
                        default:
                            this.logger.error(`[CallLogService] getContactWithNameResolution - Received directory ${origin} not managed`);
                            break;
                    }
                } catch (error) {
                    this.logger.error(`[CallLogService] getContactWithNameResolution - Directory server Get request error ${error}  `);
                    directoryContact = undefined;
                }
            }

            if (directoryContact) {
                this.logger.info(`[CallLogService] getContactWithNameResolution - Directory contact card retrieved from server and used for call log`);
                // return contact;
            }

            // No contact found with the name resolution infos (The contact may no more existe in server database),
            // If they are provided, We will use the names in the call log event

            // In this case, we create a temporary contact to be associated with this call log, dedicated and reserved for the Call Log service only.
            // PMA: TO DO LATER - Enhancement: To guarantee uniqueness and prevent it from being modified by another service,
            // its identifier will be prefixed with 'callLog-'.
            contact = this.contactService.createCallLogContact(otherPartyNumber);

            const identityFirstName = identity.attr("firstName");
            const identityLastName = identity.attr("lastName");
            const identityDisplayName = identity.attr("displayName");

            let updatedFirstName: string = "";
            let updatedLastName: string = "";
            // let updatedDisplayName: string = "";

            if (identityFirstName && identityFirstName.length) {
                updatedFirstName = identityFirstName;
            }

            if (identityLastName && identityLastName.length) {
                updatedLastName = identityLastName;
            }

            if (updatedFirstName || updatedLastName) {
                this.logger.info(`[CallLogService] getContactWithNameResolution - contact updated with identity firstname: ${anonymizer(updatedFirstName)}, lastname: ${anonymizer(updatedLastName)}`);
                identityFoundAndValid = true;
                contact.updateName(identityFirstName, identityLastName);
            } else if (identityDisplayName && identityDisplayName.length) {
                // Note: In RB Hub, for the moment, we often receive identity object, but the displayName is the number the is being called ...
                // We should therefore ignore it in case we only receive a display name identical to the initial number.
                if (identityDisplayName !== otherPartyNumber) {
                    this.logger.info(`[CallLogService] getContactWithNameResolution - contact updated with identity displayName: ${anonymizer(identityDisplayName)}`);
                    contact.updateName("", identityDisplayName);
                    identityFoundAndValid = true;
                }
            } else {
                identityFoundAndValid = false;
            }
        } else {
            // Possible scenarios:
            // - No "identity" element received in the stanza, so no xName information about the other party
            // - No right to use the name resolution provided, which may be present in the "identity" element received in the stanza.
            // In this case, we create a temporary contact to be associated with this call log, dedicated and reserved for the Call Log service only.
            // PMA: TO DO LATER - Enhancement: To guarantee uniqueness and prevent it from being modified by another service,
            // its identifier will be prefixed with 'callLog-'.
            contact = this.contactService.createCallLogContact(otherPartyNumber);
        }

        // Last options to set XName if no identity has been found (through the server name resolution or in the Call log event)
        if (!identityFoundAndValid) {
            // Search in Outlook if available (Profile MS_OUTLOOK_PLUGIN required + Desktop App only)
            if (this.centralizedService) {
                try {
                    const updateStatus = await this.centralizedService.outlook.updateContactFromOutlookInfos(contact, otherPartyNumber, true);
                    if (updateStatus) {
                        // Contact found in Outlook
                        this.logger.debug(`[CallLogService] getContactWithNameResolution - xNames updated from outlook for contact ${anonymizer(contact.id)}`);
                    }
                    else {
                        this.logger.debug(`[CallLogService] getContactWithNameResolution - No update from outlook for contact ${anonymizer(contact.id)}`);
                    }
                }
                catch (error) {
                    this.logger.debug(`[CallLogService] getContactWithNameResolution  - No outlook service available for contact ${anonymizer(contact.id)}`);
                }
            }

            // When contact displayName is a phone number,  use phoneProcan as displayName and display only that in calllog (done in callLogsCell.html)
            if (this.telephonyService?.started) {
                let displayNameisAPhoneNumber = false;
                const phoneNumberRegex = /^[0-9A-D #\-\+\*\(\)\./]{1,32}$/;
                const matchAsNumber = contact.displayName.match(phoneNumberRegex);
                displayNameisAPhoneNumber = matchAsNumber && this.telephonyService.startAsPhoneNumber(contact.displayName);
                if (displayNameisAPhoneNumber && contact.phoneProCan && contact.phoneProCan !== "") {
                    contact.displayName = contact.phoneProCan;
                }
            }
        }

        if (directoryContact) {
            return { contact: directoryContact, backupContact: contact };
        }
        return { contact: contact, backupContact: undefined };
    }

    /**
     * Create a Call log from  received call log or stanza XMPP element and add it in internal cache.
     * @param stanzaElem - The received stanza.
     * @param callLogElem - The received call log element
     */
    private async createCallLogFromStanza(stanzaElem: XMLElement, callLogElem: XMLElement): Promise<any> {
        //newCallLog++ : global parent stanzaElem could contain several call_log sections for former & new call log ways

        let trueCallLogElem = false;
        if (callLogElem) {
            trueCallLogElem = true;//direct call_logElem part was provided
        } else {
            callLogElem = stanzaElem;//else use parent stanzaElem
        }

        let ignoredByUCaaS: boolean = false;
        const id: string = callLogElem.find("call_id").text();
        const state: string = callLogElem.find("state").text();
        let origin: string = "";
        let contactId: string = "";

        // Store server name resolution, just for logs & debug purposes.
        const identity = callLogElem.find("identity");
        if (identity?.length) {
            origin = callLogElem.find("identity").attr("origin");
            contactId = callLogElem.find("identity").attr("userId");
        }

        // Check certain properties of the call log to determine whether it should be ignored by the Rainbow Web client (UCaaS)
        // --------------------------------------------------------------------------------------------------------------------
        if (state === "failed" || state === "ongoing") {
            this.logger.debug(`[CallLogService] createCallLogFromStanza --- ignore call-log received with unmanaged state ${state}`);
            ignoredByUCaaS = true;
        }

        // Ignore existing callLog - Must NOT be stored again in cache (So no creation of a new CallLogRB object)
        if (this.callLogs.some((callLog) => { return callLog.id === id; })) {
            this.logger.info(`[CallLogService] createCallLogFromStanza --- Call log id=${id} ignored (already present in internal cache)`);
            ignoredByUCaaS = true;
            this.numberDuplicatedCallLogs++;
            return false;
        }

        // Ignore Janus and  MediaPillar calllogs
        const callerJid = callLogElem.find("caller").text();
        const calleeJid = callLogElem.find("callee").text();
        if ((callerJid && callerJid.indexOf("janusgateway") !== -1) || (calleeJid && calleeJid.indexOf("janusgateway") !== -1)) {
            this.logger.debug(`[CallLogService] createCallLogFromStanza --- ignore janusgateway call-log`);
            ignoredByUCaaS = true;
        }

        if ((callerJid && this.webrtcGatewayService?.isMediaPillarJid(callerJid)) || (calleeJid && this.webrtcGatewayService?.isMediaPillarJid(calleeJid))) {
            this.logger.debug(`[CallLogService] createCallLogFromStanza --- ignore mediapillar call-log`);
            ignoredByUCaaS = true;
        }

        // Determine the type of call and the two parties involved
        // -------------------------------------------------------
        let type: string = "webrtc";
        let otherParticipantJid: any = null;
        let otherParticipantNumber: string = null;
        let calledNumber: string = null;
        const isUserContactCaller = this.xmppService.isConnectedJid(callerJid);
        const direction: CallDirection = isUserContactCaller ? CallDirection.OUTGOING : CallDirection.INCOMING;

        let conference = false;
        if (trueCallLogElem) {
            conference = callLogElem.attr("service") === "conference";
        } else {
            conference = stanzaElem.find("call_log").attr("service") === "conference";
        }

        // Handle conference calllog
        if (conference) {
            type = "conference";
            otherParticipantJid = callerJid;
            if (otherParticipantJid.indexOf("@") === -1) {
                otherParticipantNumber = otherParticipantJid;
                otherParticipantJid = null;
            }
        }
        // Handle standard calllog
        else {
            let typeCall: string;
            if (trueCallLogElem) {
                typeCall = callLogElem.attr("type");
            } else {
                typeCall = stanzaElem.find("call_log").attr("type");
            }
            if (!typeCall) { typeCall = stanzaElem.find("type").text(); }

            if (typeCall === "chat") { stanzaElem.find("call_log").attr("type"); }
            if (typeCall === "phone") { type = "telephone"; }
            otherParticipantJid = isUserContactCaller ? calleeJid : callerJid;
            if (otherParticipantJid.indexOf("@") === -1) {
                otherParticipantNumber = otherParticipantJid;
                otherParticipantJid = null;
                type = "telephone";
            }
        }

        if (otherParticipantJid || otherParticipantNumber) {
            try {
                let contact: Contact;
                let backupContact: Contact;

                if (otherParticipantJid) { // Other party is a Rainbow user
                    contact = await this.contactService.getOrCreateContact(otherParticipantJid, otherParticipantNumber);
                    this.logger.debug(`[CallLogService] createCallLogFromStanza - Contact Rainbow user id=${contact.id} - name=${contact.nameForLogs} - temp=${contact.temp}`);
                } else if (!conference) {
                    // return { contact: contact, directoryContact: directoryContact};
                    // private async getOrCreateContactWithNameResolution(callLogElem: XMLElement, otherPartyNumber: string): Promise<ICallLogItemUsedContacts> {
                    let foundContacts: IExternalLogManagedContacts = {
                        contact: undefined,
                        backupContact: undefined
                    };
                    foundContacts = await this.getOrCreateContactWithNameResolution(callLogElem, otherParticipantNumber);

                    contact = foundContacts.contact;
                    backupContact = foundContacts.backupContact;

                    // Local debug only
                    // this.logger.debug(`[CallLogService] createCallLogFromStanza - Contact Other id=${contact.id} - name=${contact.nameForLogs} - temp=${contact.temp} - origin=${origin} - id=${contactId}`);
                    // if (foundContacts.backupContact) {
                    //     this.logger.debug(`[CallLogService] createCallLogFromStanza - Contact Other Backup id=${backupContact.id} - name=${backupContact.nameForLogs} - temp=${backupContact.temp} `);
                    // }
                    this.logger.debug(`[CallLogService] createCallLogFromStanza - Contact Other id=${anonymizer(contact.id)} - name=${contact.nameForLogs} - temp=${contact.temp} - origin=${origin} - id=${contactId}`);
                    if (foundContacts.backupContact) {
                        this.logger.debug(`[CallLogService] createCallLogFromStanza - Contact Other Backup id=${anonymizer(backupContact.id)} - name=${backupContact.nameForLogs} - temp=${backupContact.temp} `);
                    }

                }

                if (type === "telephone" && !otherParticipantNumber) {
                    //try to find the number from the callee_info or the caller info depending on call direction
                    if (isUserContactCaller && stanzaElem.find("callee_info").length) {
                        otherParticipantNumber = stanzaElem.find("callee_info").attr("number");
                    } else if (!isUserContactCaller && stanzaElem.find("caller_info").length) {
                        otherParticipantNumber = stanzaElem.find("caller_info").attr("number");
                    }
                }

                if (type === "telephone" && isUserContactCaller && stanzaElem.find("callee_info")) {
                    //get called number info
                    calledNumber = stanzaElem.find("callee_info").attr("calledNumber");
                }

                // WARNING - OLD CODE KEPT FOR INFORMATION
                // The following code has been commented because it was very dangerous : it inserts old phone numbers in a rainbow contact which has no numbers anymore
                // This generates wrong call party identification for further calls to this number.
                // It seems there is no reason to implement such modifications of rainbow contact with old numbers coming from the call log entries.
                //
                // if (!conference && !contact.temp && !contact.phoneProCan && !contact.phonePbx && (otherParticipantNumber || calledNumber)) {
                //     contact.phoneProCan = otherParticipantNumber ? otherParticipantNumber : calledNumber;
                //     //workaround for capabilities calculation for contact
                //     if (!contact.phonePro) { contact.phonePro = contact.phoneProCan; }
                //     contact.phoneNumbersInitialized = true;
                //     //notify contact update (in order to refresh userInfo panel)
                //     contact.rxSubject.next({name: 'update'});
                // }
                // WARNING - OLD CODE KEPT FOR INFORMATION

                // Feature only for a call log associated to a RB user
                // Case where the correspondent is a Rainbow user but no telephone number is present in his Contact object.
                // Telephone numbers are part of the private fields of the user profile and to access them,
                //  - Both users must be in the same company (or the same organisation)
                //  - The user must be in the network of the connected user
                //  - A visibility rule has been set up between the companies of the 2 users
                // In this case, the caller's number must be stored BUT NOT in its Contact object to avoid changing the contact's profile.
                // This number is stored in a dedicated property of the current call log object and will be use afterwards as callback number
                // but it will be offered only for this call log.
                const hasContactPhoneNumber: boolean = Boolean(contact.phonePro
                    || contact.phoneProCan
                    || contact.mobilePro
                    || contact.mobileProCan
                    || contact.phonePerso
                    || contact.phonePersoCan
                    || contact.mobilePerso
                    || contact.mobilePersoCan
                    || contact.phoneInternalNumber
                    || contact.phonePbx);

                let userCallbackNumber: string = "";
                if (!conference && !hasContactPhoneNumber && !contact.temp && (otherParticipantNumber || calledNumber)) {
                    userCallbackNumber = otherParticipantNumber ? otherParticipantNumber : calledNumber;
                }

                // Reset the data if the contact associated to the call log is a directory contact
                // (It may have been set just above)
                if (this.isDirectoryContact(contact)) {
                    userCallbackNumber = "";
                }

                // Extract other callLog info from stanza
                // ------------------------------------------

                // Call log read status
                const read: boolean = callLogElem.find("ack").attr("read") === "true";

                // Call duration
                let duration: number = 0;
                if (stanzaElem.find("duration").length > 0) {
                    duration = parseInt(stanzaElem.find("duration").text(), 10);
                }

                // Call date
                const stampInfo: string = stanzaElem.find("delay").attr("stamp");
                let date: Date = null;
                if (stampInfo) {
                    date = new Date(stampInfo);
                }

                // Call Subject
                const callSubject: string = stanzaElem.find("subject").text();

                // Create the callLogObject
                // ------------------------------------------
                const callLog: CallLogRB = CallLogRB.create(id, contact, state, duration, type, read, date, direction, callSubject, ignoredByUCaaS, calledNumber, userCallbackNumber);

                // Update some additional data not done in create() method
                callLog.identityDirectory = origin;
                callLog.identityContactId = contactId;
                callLog.userOriginBackup = backupContact;

                this.callLogs.push(callLog);

                // Warning: Don't send 'ON_CALLLOG_TOTAL_NUMBER_UPDATED' to avoid having to send it
                // on each addition when retrieving N call logs from the server
                this.numberTotalCallLogs++;

                if (state === "answered" && !read) {
                    //auto ACK answered call-logs
                    this.markCallLogAsRead(callLog);
                }
                return true;
            }
            catch (error) {
                this.logger.error(`[CallLogService] createCallLogFromStanza -- failure --- ${error}`);
                throw error;
            }
        }

        return false;
    }

    /**
     * Order the call logs stored in the internal table (array) by date
     * A ON_CALLLOG_UPDATED event is sent to notify about the call logs history cache update
     * This event is sent only if the classification does not follow a explicit fetch request.
     * Update the counter of missed calls (state === "missed" &&  CallDirection.INCOMING)
     * The number of missed calls logs is then checked again on internal cache and the associated counter may be updated
     * If the case, an event 'ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED' is sent with the new value.
     */
    private orderCallLogs(): void {
        this.callLogs.sort((a: CallLogRB, b: CallLogRB) => { return CallLogRB.getDate(b) - CallLogRB.getDate(a); });

        this.aggregateCalllogs();

        // Check if the received call logs comes from a fetch user request (initCallLogs, getNextCallLogs)
        // If the case, no need to send additional event especially to the SDK Client
        // This makes it possible to distinguish between call logs initially retrieved and those added later
        // as a result of XMPP notifications
        if (!this.userFetchRequest) {
            this.logger.debug(`[CallLogService] orderCallLogs -- Send event ON_CALLLOG_UPDATED`);
            this.sendEvent(CallLogServiceEvents.ON_CALLLOG_UPDATED);
        }

        const missedCallLogsInCache = this.getMissedCallLogsInCacheCounter();
        if (missedCallLogsInCache !== this.numberMissedCallLogs) {
            this.logger.info(`[CallLogService] orderCallLogs -- Missed Call Logs Total=${this.numberMissedCallLogs} -- In Cache=${missedCallLogsInCache}`);
            this.numberMissedCallLogs = missedCallLogsInCache;
            this.sendEvent(CallLogServiceEvents.ON_CALLLOG_MISSEDCALLS_COUNTER_UPDATED, this.numberMissedCallLogs);
        }
    }

    /**
     * Build the table of aggregated call logs
     * The aggregated call logs are deduced from the available call logs (obtained with the 'getCalllogs' method) but the items
     * are grouped by user according to different information such as status ("missed" or not), direction ("incoming", "outgoing")
     */
    private aggregateCalllogs(): void {
        let current: number = 0;
        this.aggregatedCallLogs = [];
        const passed: any = {};
        // this.callLogs.filter(((callLog: CallLogRB) => { return !callLog.isIgnoredByUCaaS; })).forEach((callLog: CallLog, ind: number) => {
        this.callLogs.forEach((callLog: CallLogRB, ind: number) => {
            // Skip call logs that must be ignored by UCaaS (Ex: Janus and  MediaPillar calllogs)
            if (callLog.isIgnoredByUCaaS) { return false; }

            // const jid = callLog.user.id;
            let jid = callLog.user.id;

            if (callLog.directoryContactId) {
                jid = callLog.directoryContactId;
                // this.contact.personalDirectoryFlag && (callLog.directoryContactId === this.contact.id))) {
            }

            // Handle conference calllog
            if (callLog.type === CallLogType.CONFERENCE) {
                if (ind !== 0) { current++; }
                this.aggregatedCallLogs[current] = callLog;
                this.aggregatedCallLogs[current].count = 1;
                this.aggregatedCallLogs[current].editable = false;
            }

            // First index
            else if (ind === 0) {
                passed[jid] = 1;
                this.aggregatedCallLogs[current] = callLog;
                this.aggregatedCallLogs[current].count = 1;
                this.aggregatedCallLogs[current].editable = true;
                if (callLog.state === "missed" && callLog.direction === CallDirection.INCOMING) { this.aggregatedCallLogs[current].isMissed = true; }
                else if (callLog.state === "missed" && callLog.direction === CallDirection.OUTGOING) { this.aggregatedCallLogs[current].isNotAnswered = true; }
            }
            // Other index
            else {
                // if we already have this element
                if (passed[jid]) {
                    const index = passed[jid];
                    const element = this.aggregatedCallLogs[index - 1];
                    if (element.editable) {
                        if (element.isMissed && callLog.state === "missed" && callLog.direction === CallDirection.INCOMING) { element.count++; }
                        else if (element.isNotAnswered && callLog.state === "missed" && callLog.direction === CallDirection.OUTGOING) { element.count++; }
                        else { element.editable = false; }
                    }
                }

                // New element
                else {
                    current++;
                    passed[jid] = current + 1;
                    this.aggregatedCallLogs[current] = callLog;
                    this.aggregatedCallLogs[current].count = 1;
                    this.aggregatedCallLogs[current].editable = true;
                    if (callLog.state === "missed" && callLog.direction === CallDirection.INCOMING) { this.aggregatedCallLogs[current].isMissed = true; }
                    else if (callLog.state === "missed" && callLog.direction === CallDirection.OUTGOING) { this.aggregatedCallLogs[current].isNotAnswered = true; }
                }
            }
        });
    }

    /**
     * Check if the specified contact is a directory contact i.e. coming from one of the following directories:
     * - Personal directory / Business directory / Microsoft O365 contacts/AD users
     * - PBX phonebook / Outlook (for Desktop only)
     * @param contact - The contact to check.
     */
    private isDirectoryContact(contact: Contact): boolean {
        if (!contact) {
            return false;
        }

        return (contact.personalDirectoryFlag || contact.companyDirectoryFlag
                || contact.o365Flag || contact.phonebookDirectoryFlag || contact.outlookFlag);
    }

    /**
     * Get a callLog entry from calLogs array by its id
     * @param id - The call log Id
     */
    public getCallLogById(id: string): CallLogRB {
        return (this.callLogs || []).find((callLog) => { return callLog.id === id; });
    }

    // ------------------------------------------
    //          METHODS FOR SDKv2
    // ------------------------------------------

    /**
     * Initializes the CallLog service for the user by returning the most recent call logs.
     * The number of call logs to be returned can optionnaly be specified in the request
     * Additional call logs have to be retrieved furthermore via the API 'getNextCallLogs'.
     * @param number - (optional) Number of call logs to retrieve at initialization step.
     * This parameter is optional and set by default to 100.
     * The maximum value allowed is 250. If the value supplied is greater, the maximum value will be used.
     * @returns Returns a Promise with an array of the first retrieved call logs
     */
    public async initCallLogs(number: number = CallLogServiceRB.NB_DEF_CALL_LOGS): Promise<CallLog[]> {
        this.logger.info(`[CallLogService] initCallLogs --  number requested = ${number}`);

        try {
            if (this.initialized) {
                const error = new Error("[CallLogService] initCallLogs - The service is already initialized !");
                this.logger.error(error.message);
                return Promise.reject(error);
            }

            this.userFetchRequest = true;

            // Check requested nb of additional call logs
            if (number <= 0) {
                throw new Error(`initCallLogs -- Value of 'number' option (${number}) is not valid !`);
            }

            // The number of call logs to retrieve from the server is limited to NB_MAX_NEXT_CALL_LOGS (250)
            if (number > CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS) {
                number = CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS;
            }

            const fetchOptions: IFetchOptions = {};
            fetchOptions.numberOfElementsToGet = number;

            // This is the first time, the call-logs will be retrieved from the server
            await this.initialize(fetchOptions);
            //
            const callLogs: CallLogRB[] = this.callLogs;
            // const callLogs: CallLog[] = await this.orderCallLogsHistory();

            this.logger.info(`[CallLogService] initCallLogs -- Return ${callLogs.length} entries`);
            this.userFetchRequest = false;
            return callLogs;
        }
        catch (error) {
            this.logger.error(`[CallLogService] === initCallLogs FAILURE === ${error.message}`);
            throw error;
        }
    }

    /**
     * Get the current content of the call logs history.
     * The request can be made at any time to retrieve the call logs already known by the Call Log service.
     * It is also advisable to make this request when an 'ON_CALLLOG_UPDATED' event has been received notifying you
     * that the call logs history has been updated following an operation such as the reception of a brand new call log,
     * the modification (e.g., marking as read) or the deletion of existing call log(s).
     * @returns Returns a Promise with an array of the call logs already known by the Call Log service
     */
    public async getCallLogs(): Promise<CallLog[]> {
        const callLogs = await this.orderCallLogsHistory();
        this.logger.info(`[CallLogService] getCallLogs -- Return ${callLogs.length} entries`);
        return callLogs;
    }

    /**
     * Get additional call logs.
     * The number of additional call logs to be returned can be specified in the request
     * @param number - (optional) Number of additional call logs to get.
     * This parameter is optional and set by default to 100.
     * The allowed range of values is [1..250].
     * If the value provided is greater, the maximum value will be used.
     * If the value is 0 or less, the request will be rejected and an error thrown.
     * @returns Returns a Promise with an array of the call logs already known by the Call Log service and the newly retrieved.
     * @throws Error - In the event of the specified parameter is invalid.
     */
    public async getNextCallLogs(number: number = CallLogServiceRB.NB_DEF_CALL_LOGS): Promise<CallLog[]> {
        try {
            this.logger.info(`[CallLogService] getNextCallLogs -- number requested = ${number}`);
            this.userFetchRequest = true;

            // Check requested nb of additional call logs
            if (number <= 0) {
                throw new Error(`getNextCallLogs -- Value of 'number' option (${number}) is not valid !`);
            }

            // The number of call logs to retrieve from the server is limited to NB_MAX_NEXT_CALL_LOGS (250)
            if (number > CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS) {
                number = CallLogServiceRB.NB_MAX_NEXT_CALL_LOGS;
            }

            const callLogs: CallLog[] = await this.fetchNextPage(number);
            this.userFetchRequest = false;
            return callLogs;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getNextCallLogs');
            this.logger.error(`[CallLogService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }
}
